import moment from 'moment-timezone';

/**
 * How the sessions Auto-plan just made come onto the calendar once its dialog has closed: a wave (Petr, 2026-10-08,
 * picked from clickable samples to be more noticeable than the plain fade of 2026-10-07). The week fills day by day,
 * each day's sessions one after another, each rising in with a small pop, then a light shine sweeps across it. Extra
 * time that comes with them fades in with the session before it, so a gap shows with the session that leaves it, not
 * before it or all at once at the end.
 *
 * Only moves, a scale and a fade, and a moving shine, which the browser runs on its own: the slide-out and glowing
 * double pulse of before were redrawn by the page every frame on every new session, just as the calendar redrew with
 * the new plan, and could stutter on a slow computer.
 *
 * The saga tags the new sessions (`autoPlanIntro`, with each one's `autoPlanDelayMs`) and lists their turns
 * (`autoPlanIntroSteps`); the converter gives sessions a class, `eventDidMount` sets each one's wait as a CSS variable,
 * and calendarStyles runs the animations. Every wait counts from when the sessions first appear on the calendar, not
 * from when the saga tagged them: drawing a busy week took half a second, and extra time timed from the saga came in
 * that much early, partway through the sessions, at a moment that depended on the computer.
 */
export const AUTO_PLAN_INTRO = {
  // How long a session takes to rise in and settle.
  inMs: 440,
  // The shine starts this long after a session begins to come in, once it has landed, and crosses it in shineMs.
  shineAfterMs: 280,
  shineMs: 650,
  // Each day starts this long after the one before it...
  dayStepMs: 95,
  // ...closer when the dates span many days (month view): the last day never starts later than this.
  daysSpreadMaxMs: 650,
  // Within a day, each session starts this long after the one before it, up to the spread.
  withinDayStepMs: 25,
  withinDaySpreadMaxMs: 150
};

/** How many days after the first one each of these starts falls, in the student's time zone. */
export const autoPlanDays = (starts: Array<string | Date | number>, timeZone?: string): number[] => {
  const dayOf = (start: string | Date | number) => (timeZone ? moment.tz(start, timeZone) : moment(start)).startOf('day');
  const first = starts.length ? dayOf(starts[0]) : undefined;
  return starts.map(start => (first ? Math.round(dayOf(start).diff(first, 'days', true)) : 0));
};

/** When each session starts coming in, given its day (`autoPlanDays`), for sessions in time order. */
export const autoPlanDelays = (days: number[]): number[] => {
  const lastDay = days.length ? days[days.length - 1] : 0;
  const dayStep = lastDay > 0 ? Math.min(AUTO_PLAN_INTRO.dayStepMs, AUTO_PLAN_INTRO.daysSpreadMaxMs / lastDay) : 0;
  let rank = 0;
  return days.map((day, index) => {
    rank = index > 0 && days[index - 1] === day ? rank + 1 : 0;
    const withinDay = Math.min(rank * AUTO_PLAN_INTRO.withinDayStepMs, AUTO_PLAN_INTRO.withinDaySpreadMaxMs);
    return Math.round(day * dayStep + withinDay);
  });
};

// When the last shine is over, and the tags can come off. Counted from when the saga tags the sessions, so it leaves
// room for the calendar to draw them first (half a second on a busy week): taking the tags off redraws every session,
// and doing it sooner could cut the last shines short on a slow computer.
export const AUTO_PLAN_INTRO_TOTAL_MS =
  AUTO_PLAN_INTRO.daysSpreadMaxMs +
  AUTO_PLAN_INTRO.withinDaySpreadMaxMs +
  AUTO_PLAN_INTRO.shineAfterMs +
  AUTO_PLAN_INTRO.shineMs +
  700;

/** Auto-plan's new sessions in time order: when each starts (epoch ms), and its turn to come in (`autoPlanDelays`). */
export type AutoPlanIntroSteps = Array<{ start: number; delayMs: number }>;

/**
 * When extra time starting at `start` comes in: once the new session before it is half in, or with the first one if
 * it comes before them all.
 */
export const extraTimeDelay = (steps: AutoPlanIntroSteps, start: number): number => {
  let turn = steps.length ? steps[0].delayMs : 0;
  for (const step of steps) {
    if (step.start > start) break;
    turn = step.delayMs;
  }
  return turn + AUTO_PLAN_INTRO.inMs / 2;
};
